import { useEffect } from 'react';

interface UseKeyboardPaginationOptions {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  enabled?: boolean;
}

/**
 * Hook to add keyboard shortcuts for pagination navigation
 * - Arrow Left: Previous page
 * - Arrow Right: Next page
 * - Home: First page
 * - End: Last page
 */
export function useKeyboardPagination({
  currentPage,
  totalPages,
  onPageChange,
  enabled = true,
}: UseKeyboardPaginationOptions) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Don't trigger if modifier keys are pressed (except for Home/End)
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          if (currentPage > 1) {
            e.preventDefault();
            onPageChange(currentPage - 1);
          }
          break;
        case 'ArrowRight':
          if (currentPage < totalPages) {
            e.preventDefault();
            onPageChange(currentPage + 1);
          }
          break;
        case 'Home':
          if (currentPage !== 1) {
            e.preventDefault();
            onPageChange(1);
          }
          break;
        case 'End':
          if (currentPage !== totalPages) {
            e.preventDefault();
            onPageChange(totalPages);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, totalPages, onPageChange, enabled]);
}
